import { create } from "zustand";
import { supabase } from "@/lib/supabase";
import { uploadAudioBlob, transcribeMeeting, summarizeMeeting } from "@/lib/api";
import type { Meeting, TemplateKey, DetailLevel } from "@/types";
import i18n from "@/lib/i18n";

interface MeetingState {
  meetings: Meeting[];
  loading: boolean;
  error: string | null;
  fetchMeetings: () => Promise<void>;
  createMeeting: (data: {
    title?: string;
    template_key: TemplateKey;
    context?: string;
    detail_level: DetailLevel;
    duration_seconds?: number;
    enable_diarization?: boolean;
  }) => Promise<Meeting>;
  deleteMeeting: (id: string) => Promise<void>;
  retryMeeting: (id: string) => Promise<void>;
  processMeeting: (
    meetingId: string,
    audioBlob: Blob,
    mimeType: string,
    templateKey: TemplateKey,
    context?: string,
    detailLevel?: DetailLevel
  ) => Promise<void>;
}

export const useMeetingStore = create<MeetingState>((set, get) => ({
  meetings: [],
  loading: false,
  error: null,

  fetchMeetings: async () => {
    set({ loading: true, error: null });
    const { data, error } = await supabase
      .from("meetings")
      .select("*")
      .order("recorded_at", { ascending: false });

    if (error) {
      set({ error: error.message, loading: false });
      return;
    }
    set({ meetings: data ?? [], loading: false });
  },

  createMeeting: async (data) => {
    const {
      data: { user },
    } = await supabase.auth.getUser();
    if (!user) throw new Error("Not authenticated");

    const { data: meeting, error } = await supabase
      .from("meetings")
      .insert({
        user_id: user.id,
        title: data.title ?? null,
        template_key: data.template_key,
        context: data.context ?? null,
        detail_level: data.detail_level,
        duration_seconds: data.duration_seconds ?? null,
        language: i18n.language,
        status: "recorded",
      })
      .select()
      .single();

    if (error) throw error;
    set({ meetings: [meeting, ...get().meetings] });
    return meeting;
  },

  deleteMeeting: async (id) => {
    const { error } = await supabase.from("meetings").delete().eq("id", id);
    if (error) throw error;
    set({ meetings: get().meetings.filter((m) => m.id !== id) });
  },

  retryMeeting: async (id) => {
    const { data: transcript } = await supabase
      .from("transcripts")
      .select("id")
      .eq("meeting_id", id)
      .maybeSingle();

    const { data: meeting } = await supabase
      .from("meetings")
      .select("*")
      .eq("id", id)
      .single();

    if (!meeting) return;

    if (transcript) {
      await supabase
        .from("meetings")
        .update({ status: "summarizing", error_message: null })
        .eq("id", id);
      await summarizeMeeting({
        meeting_id: id,
        template_key: meeting.template_key,
        context: meeting.context ?? undefined,
        detail_level: meeting.detail_level,
      });
    } else {
      await supabase
        .from("meetings")
        .update({ status: "transcribing", error_message: null })
        .eq("id", id);
      await transcribeMeeting({ meeting_id: id });
      await summarizeMeeting({
        meeting_id: id,
        template_key: meeting.template_key,
        context: meeting.context ?? undefined,
        detail_level: meeting.detail_level,
      });
    }
  },

  processMeeting: async (
    meetingId,
    audioBlob,
    mimeType,
    templateKey,
    context,
    detailLevel
  ) => {
    const audioPath = await uploadAudioBlob(meetingId, audioBlob, mimeType);

    await supabase
      .from("meetings")
      .update({ audio_path: audioPath, status: "transcribing" })
      .eq("id", meetingId);

    await transcribeMeeting({ meeting_id: meetingId });
    await summarizeMeeting({
      meeting_id: meetingId,
      template_key: templateKey,
      context,
      detail_level: detailLevel,
    });
  },
}));
